import { useState } from "react";
import { PageHeader, GlassCard, EmptyState, useCrud, ItemDialog, RowActions } from "../_shared";
import { Button } from "@/components/ui/button";
import { Plus, Star } from "lucide-react";

const FIELDS = [
  { key: "name", label: "Customer name", required: true, placeholder: "e.g. Rahul Das" },
  { key: "title", label: "Sub-title (optional)", placeholder: "e.g. Truck operator, Bally" },
  { key: "quote", label: "Testimonial", required: true, type: "textarea", rows: 4 },
  { key: "rating", label: "Rating (1–5)", type: "number", placeholder: "5" },
  { key: "photo", label: "Customer photo URL (optional)", type: "image" },
  { key: "enabled", label: "Visible on site", type: "switch" },
];

export default function TestimonialsManager() {
  const { items, loading, busy, create, update, remove, move } = useCrud("/admin/cms/testimonials");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const openNew = () => { setEditing({ enabled: true, rating: 5 }); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setOpen(true); };
  const submit = async (form) => {
    if (editing?.id) await update(editing.id, form); else await create(form);
    setOpen(false);
  };

  return (
    <>
      <PageHeader
        title="Testimonials"
        subtitle="Real customer quotes shown in the Reviews section. Please replace all 'placeholder' entries with genuine Google reviews."
        actions={<Button onClick={openNew} className="bg-[#0057B8] hover:bg-[#003F87] text-white rounded-full" data-testid="new-testimonial-btn"><Plus className="w-4 h-4 mr-1" /> New Testimonial</Button>}
      />
      {loading ? <div className="text-slate-500">Loading…</div> : items.length === 0 ? (
        <EmptyState label="No testimonials yet." action={<Button onClick={openNew}>Add first testimonial</Button>} />
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {items.map((it, i) => (
            <GlassCard key={it.id} className="p-5">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-3">
                  {it.photo ? (
                    <img src={it.photo} alt="" className="w-11 h-11 rounded-full object-cover border border-slate-200" />
                  ) : (
                    <div className="w-11 h-11 rounded-full bg-[#0057B8] text-white grid place-items-center font-bold">{(it.name || "?").charAt(0)}</div>
                  )}
                  <div>
                    <div className="font-semibold text-slate-900">{it.name}</div>
                    {it.title && <div className="text-xs text-slate-500">{it.title}</div>}
                    <div className="flex gap-0.5 text-[#F59E0B] mt-1">
                      {Array.from({ length: it.rating || 0 }).map((_, k) => <Star key={k} className="w-3 h-3 fill-[#F59E0B]" />)}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {!it.enabled && <span className="text-[10px] font-semibold bg-slate-200 text-slate-600 px-2 py-0.5 rounded-full">Hidden</span>}
                  <RowActions
                    onEdit={() => openEdit(it)} onDelete={() => remove(it.id)}
                    onUp={() => move(it.id, "up")} onDown={() => move(it.id, "down")}
                    canUp={i > 0} canDown={i < items.length - 1}
                  />
                </div>
              </div>
              <p className="mt-3 text-sm text-slate-600 leading-relaxed">"{it.quote}"</p>
            </GlassCard>
          ))}
        </div>
      )}
      <ItemDialog
        open={open} onOpenChange={setOpen}
        title={editing?.id ? "Edit Testimonial" : "New Testimonial"}
        fields={FIELDS}
        initial={editing || { enabled: true, rating: 5 }}
        onSubmit={submit}
        submitting={busy}
      />
    </>
  );
}
